import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ConsentService } from '@services/common/consent.service';
import { privacyPolicyUrl } from '@services/common/cookie.service';
import { CustomTranslateService } from '@services/common/custom-translate.service';

/**
 * Lets someone revisit the cookie choice they already made.
 *
 * The banner is a one-shot question — it can only ask again, never show what was
 * answered. This dialog opens with the current setting reflected in the toggle,
 * so the state is visible before anything is changed, and nothing is written
 * until Save.
 */
@Component({
    selector: 'app-cookie-preferences',
    templateUrl: './cookie-preferences.component.html',
    styleUrls: ['./cookie-preferences.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CookiePreferencesComponent {
    /**
     * Draft state, not the live setting: the toggle is seeded from the stored
     * decision each time the dialog opens, and only Save commits it. Closing with
     * the X or Escape leaves the current consent untouched.
     */
    public analytics = false;

    @Input()
    public set visible(value: boolean) {
        if (value && !this.isVisible) {
            this.analytics = this.consentService.hasAnalyticsConsent;
        }
        this.isVisible = value;
    }
    public get visible(): boolean {
        return this.isVisible;
    }

    @Output() public visibleChange = new EventEmitter<boolean>();

    private isVisible = false;

    constructor(
        private readonly consentService: ConsentService,
        private readonly translateService: CustomTranslateService,
    ) {}

    /** Same destination as the banner's link — the cookies section of the notice. */
    public get policyUrl(): string {
        return privacyPolicyUrl(this.translateService.currentLang);
    }

    public save(): void {
        this.consentService.setAnalyticsConsent(this.analytics);
        this.close();
    }

    public close(): void {
        this.isVisible = false;
        this.visibleChange.emit(false);
    }
}
